<script setup lang="ts">
import { computed, onUnmounted, reactive, ref, watch } from 'vue';
import { defaultAccountSettings } from '@atum-remote/preferences';
import type { RemoteUiAdapter } from '../model.js';
import { useAccountPreferences } from '../lib/account-preferences.js';
import {
  deviceColorLabel,
  deviceColors,
  type DeviceColor,
  type DirectoryPreferences,
} from '../lib/customization.js';
import { initials, pluralize } from '../lib/format.js';
import { useNativeUpdates } from '../lib/native-updates.js';
import type { SettingsTab } from '../lib/route.js';
import AppIcon from './AppIcon.vue';
import BrandMark from './BrandMark.vue';
import NativeUpdatesPanel from './NativeUpdatesPanel.vue';
import type { IconName } from './icons.js';

/**
 * Settings is a small set of pages, one per concern: the account and how the
 * person appears, how the directory is laid out, terminal text, and (in the
 * installed app) SSH connections and updates. Appearance lives on the sidebar.
 */
const props = defineProps<{
  adapter: RemoteUiAdapter;
  connected: boolean;
  accountName: string | undefined;
  signInConfigured: boolean;
  busy: boolean;
  preferencesBusy?: boolean;
  canImport?: boolean;
  tab: SettingsTab;
  directory: DirectoryPreferences;
}>();
const emit = defineEmits<{
  signIn: [];
  signOut: [];
  importBrowserSettings: [];
  navigate: [tab: SettingsTab];
  updateDirectory: [patch: Partial<DirectoryPreferences>];
}>();
const account = useAccountPreferences();
const nativeUpdates = useNativeUpdates(props.adapter.updater);
const web = props.adapter.kind === 'web';

interface Tab {
  readonly id: SettingsTab;
  readonly label: string;
  readonly icon: IconName;
}
const tabs = computed<readonly Tab[]>(() => [
  ...(props.adapter.supportsSignIn
    ? [{ id: 'account', label: 'Account', icon: 'user' } as const]
    : []),
  { id: 'directory', label: 'Directory', icon: 'devices' },
  ...(web ? [{ id: 'terminal', label: 'Terminal', icon: 'terminal' } as const] : []),
  ...(!web
    ? [
        { id: 'connections', label: 'Connections', icon: 'key' } as const,
        { id: 'updates', label: 'Updates', icon: 'download' } as const,
      ]
    : []),
  { id: 'about', label: 'About', icon: 'info' },
]);
const active = computed<SettingsTab>(() =>
  tabs.value.some((tab) => tab.id === props.tab) ? props.tab : (tabs.value[0]?.id ?? 'about'),
);

// Account: who you are comes from ATUM Auth; how you appear here is yours to set.
const accountSettings = computed(
  () => account?.state.value.settings.account ?? defaultAccountSettings(),
);
const profile = reactive<{ displayName: string; avatarColor: DeviceColor }>({
  displayName: '',
  avatarColor: 'mint',
});
let profileBase = defaultAccountSettings() as { displayName: string; avatarColor: DeviceColor };
let profileRevision = 0;
let profileInitialized = false;
watch(
  () => [accountSettings.value, account?.state.value.revision ?? 0] as const,
  ([value, revision]) => {
    if (
      profileInitialized &&
      (profile.displayName !== profileBase.displayName ||
        profile.avatarColor !== profileBase.avatarColor)
    )
      return;
    profile.displayName = value.displayName;
    profile.avatarColor = value.avatarColor;
    profileBase = { ...value };
    profileRevision = revision;
    profileInitialized = true;
  },
  { immediate: true },
);
const cleanName = computed(() => profile.displayName.trim().replace(/\s+/g, ' '));
const profileDirty = computed(
  () =>
    cleanName.value !== accountSettings.value.displayName ||
    profile.avatarColor !== accountSettings.value.avatarColor,
);
const shownName = computed(() => cleanName.value || props.accountName || '');
const avatar = computed(() => initials(shownName.value));
const profileError = ref('');
const profileSaved = ref(false);
let savedTimer: ReturnType<typeof setTimeout> | undefined;
async function saveProfile() {
  if (!account || !profileDirty.value) return;
  profileError.value = '';
  try {
    await account.update(
      {
        type: 'settings',
        patch: { account: { displayName: cleanName.value, avatarColor: profile.avatarColor } },
      },
      profileRevision,
    );
    resetProfile();
    profileSaved.value = true;
    clearTimeout(savedTimer);
    savedTimer = setTimeout(() => {
      profileSaved.value = false;
    }, 4000);
  } catch (cause) {
    profileError.value = cause instanceof Error ? cause.message : 'Profile could not be saved.';
  }
}
function resetProfile() {
  profile.displayName = accountSettings.value.displayName;
  profile.avatarColor = accountSettings.value.avatarColor;
  profileBase = { ...accountSettings.value };
  profileRevision = account?.state.value.revision ?? 0;
  profileError.value = '';
}
onUnmounted(() => clearTimeout(savedTimer));
const accessLabel = computed(() =>
  props.adapter.accountAccess === 'personal'
    ? 'Personal access'
    : props.adapter.accountAccess === 'owner'
      ? 'Owner account'
      : props.adapter.accountAccess === 'staff'
        ? 'Shared device access'
        : 'Signed-in account',
);

// Terminal text and keys, saved to the account and used by every session tab.
const terminal = computed(
  () => account?.state.value.settings.terminal ?? { fontSize: 15, keyBar: null },
);
const terminalError = ref('');
async function patchTerminal(patch: { fontSize?: number; keyBar?: boolean | null }) {
  if (!account) return;
  terminalError.value = '';
  try {
    await account.update({ type: 'settings', patch: { terminal: patch } });
  } catch (cause) {
    terminalError.value =
      cause instanceof Error ? cause.message : 'Terminal settings could not be saved.';
  }
}
const keyBarChoice = computed({
  get: () => (terminal.value.keyBar === null ? 'auto' : terminal.value.keyBar ? 'always' : 'never'),
  set: (value: 'auto' | 'always' | 'never') =>
    void patchTerminal({ keyBar: value === 'auto' ? null : value === 'always' }),
});
const keyBarOptions: readonly { value: 'auto' | 'always' | 'never'; label: string }[] = [
  { value: 'auto', label: 'Automatic' },
  { value: 'always', label: 'Always' },
  { value: 'never', label: 'Never' },
];

// Directory layout is a live preference; the directory page mirrors it immediately.
const collapsedCount = computed(() => props.directory.collapsed.length);
const scope = web ? 'your ATUM account' : 'this computer';
</script>

<template>
  <div class="settings">
    <nav class="tab-strip settings-tabs" aria-label="Settings sections">
      <button
        v-for="item in tabs"
        :key="item.id"
        type="button"
        class="tab"
        :aria-current="active === item.id ? 'page' : undefined"
        @click="emit('navigate', item.id)"
      >
        <AppIcon :name="item.icon" />{{ item.label
        }}<span
          v-if="item.id === 'updates' && nativeUpdates.state.phase === 'available'"
          class="tag soon"
          >New</span
        >
      </button>
    </nav>

    <!-- Account -->
    <div v-if="active === 'account'" class="page-stack">
      <section v-if="!props.connected" class="panel account-signin">
        <span class="avatar lg" data-color="slate" aria-hidden="true"><AppIcon name="user" /></span>
        <div>
          <h2>Not signed in</h2>
          <p>Sign in with your ATUM account to see the devices shared with you.</p>
        </div>
        <button
          type="button"
          class="primary"
          :disabled="props.busy || !props.signInConfigured"
          @click="emit('signIn')"
        >
          <AppIcon name="key" />Continue with ATUM Auth
        </button>
        <p v-if="!props.signInConfigured && !props.busy" class="hint">
          Owner sign-in is not configured on this server yet.
        </p>
      </section>
      <template v-else>
        <section class="panel account-hero">
          <span class="avatar lg" :data-color="profile.avatarColor" aria-hidden="true">{{
            avatar
          }}</span>
          <div class="account-hero-text">
            <h2>{{ shownName }}</h2>
            <p>
              <span v-if="cleanName" class="mono">{{ props.accountName }}</span>
              <span v-else>Signed in with ATUM Auth</span>
              <span class="tag"><AppIcon name="shield" />{{ accessLabel }}</span>
            </p>
          </div>
          <button type="button" :disabled="props.busy" @click="emit('signOut')">
            <AppIcon name="logout" />Sign out
          </button>
        </section>

        <form class="panel" @submit.prevent="saveProfile">
          <div class="section-head">
            <span class="section-icon"><AppIcon name="edit" /></span>
            <div>
              <h2>Profile</h2>
              <p>How you appear in ATUM Remote. Your sign-in identity itself lives at ATUM Auth.</p>
            </div>
          </div>
          <div v-if="profileError" class="banner error" role="alert">
            <AppIcon name="warning" /><span>{{ profileError }}</span>
          </div>
          <div class="settings-form">
            <label class="field">
              <span class="field-label">Display name</span>
              <input
                v-model="profile.displayName"
                maxlength="60"
                :placeholder="props.accountName"
                autocomplete="nickname"
                :disabled="props.preferencesBusy"
              />
              <span class="field-help">Leave empty to use your ATUM account name.</span>
            </label>
            <fieldset class="picker color-picker" :disabled="props.preferencesBusy">
              <legend class="field-label">Avatar color</legend>
              <label
                v-for="color in deviceColors"
                :key="color"
                :data-color="color"
                :title="deviceColorLabel[color]"
              >
                <input
                  v-model="profile.avatarColor"
                  type="radio"
                  name="avatar-color"
                  :value="color"
                />
                <span class="swatch" aria-hidden="true" />
                <span class="sr-only">{{ deviceColorLabel[color] }}</span>
              </label>
            </fieldset>
          </div>
          <footer class="settings-actions">
            <span v-if="profileSaved" class="settings-saved" role="status">
              <AppIcon name="check" />Saved
            </span>
            <button
              type="button"
              class="quiet"
              :disabled="!profileDirty || props.preferencesBusy"
              @click="resetProfile"
            >
              Discard
            </button>
            <button
              type="submit"
              class="primary"
              :disabled="!profileDirty || props.preferencesBusy"
            >
              Save profile
            </button>
          </footer>
        </form>

        <section class="panel">
          <div class="section-head">
            <span class="section-icon"><AppIcon name="clock" /></span>
            <div>
              <h2>Session</h2>
              <p>
                You stay signed in while you use this tab. After 30 minutes without activity, or
                when a closed tab is reopened, ATUM Auth asks you to sign in again.
              </p>
            </div>
          </div>
          <dl class="facts">
            <div>
              <dt>Signed in as</dt>
              <dd class="mono">{{ props.accountName }}</dd>
            </div>
            <div>
              <dt>Access</dt>
              <dd>{{ accessLabel }}</dd>
            </div>
            <div>
              <dt>Other ATUM apps</dt>
              <dd>Stay signed in when you sign out here</dd>
            </div>
          </dl>
        </section>

        <section class="panel">
          <div class="section-head">
            <span class="section-icon"><AppIcon name="cloud" /></span>
            <div>
              <h2>Your data</h2>
              <p>
                Device names, groups, icons and colors, directory layout, appearance, terminal text
                and this profile are saved to your ATUM account and follow you to other browsers.
                Device identity and access permissions never change here.
              </p>
            </div>
          </div>
          <div v-if="props.canImport" class="callout">
            <AppIcon name="info" />
            <div>
              <strong>Bring settings from this browser</strong>
              <p>
                This account is empty. Import the names and layout saved in this browser; only
                devices currently in your directory are included, and nothing is deleted.
              </p>
              <div class="button-row">
                <button
                  type="button"
                  :disabled="props.preferencesBusy || props.busy"
                  @click="emit('importBrowserSettings')"
                >
                  <AppIcon name="upload" />Import browser settings
                </button>
              </div>
            </div>
          </div>
        </section>
      </template>
    </div>

    <!-- Directory -->
    <div v-else-if="active === 'directory'" class="page-stack">
      <section class="panel">
        <div class="section-head">
          <span class="section-icon"><AppIcon name="grid" /></span>
          <div>
            <h2>Layout</h2>
            <p>How the device list opens. You can still switch from the directory itself.</p>
          </div>
        </div>
        <div class="settings-rows">
          <div class="settings-row">
            <div class="settings-row-text">
              <strong>View</strong>
              <span>Cards with actions, or compact rows.</span>
            </div>
            <fieldset class="segmented" :disabled="props.preferencesBusy">
              <legend class="sr-only">View</legend>
              <label>
                <input
                  type="radio"
                  name="directory-view"
                  value="grid"
                  :checked="props.directory.view === 'grid'"
                  @change="emit('updateDirectory', { view: 'grid' })"
                />
                <AppIcon name="grid" />Grid
              </label>
              <label>
                <input
                  type="radio"
                  name="directory-view"
                  value="list"
                  :checked="props.directory.view === 'list'"
                  @change="emit('updateDirectory', { view: 'list' })"
                />
                <AppIcon name="list" />List
              </label>
            </fieldset>
          </div>
          <div class="settings-row">
            <div class="settings-row-text">
              <strong>Sort by</strong>
              <span>Online devices first, or the ones you used most recently.</span>
            </div>
            <fieldset class="segmented" :disabled="props.preferencesBusy">
              <legend class="sr-only">Sort</legend>
              <label
                v-for="option in [
                  { value: 'name', label: 'Name' },
                  { value: 'status', label: 'Status' },
                  { value: 'seen', label: 'Last seen' },
                ] as const"
                :key="option.value"
              >
                <input
                  type="radio"
                  name="directory-sort"
                  :value="option.value"
                  :checked="props.directory.sort === option.value"
                  @change="emit('updateDirectory', { sort: option.value })"
                />{{ option.label }}
              </label>
            </fieldset>
          </div>
          <div class="settings-row">
            <div class="settings-row-text">
              <strong>Groups</strong>
              <span>
                {{
                  collapsedCount
                    ? `${pluralize(collapsedCount, 'group')} collapsed`
                    : 'Every group is expanded.'
                }}
              </span>
            </div>
            <button
              type="button"
              class="small"
              :disabled="!collapsedCount || props.preferencesBusy"
              @click="emit('updateDirectory', { collapsed: [] })"
            >
              Expand all
            </button>
          </div>
        </div>
      </section>
      <section class="panel">
        <div class="section-head">
          <span class="section-icon"><AppIcon name="tag" /></span>
          <div>
            <h2>Names, groups and icons</h2>
            <p>
              Customize any device from its card or page. Nicknames, groups, icons and colors are
              saved for {{ scope }} and never change the device itself or who can reach it.
            </p>
          </div>
        </div>
      </section>
    </div>

    <!-- Terminal -->
    <div v-else-if="active === 'terminal'" class="page-stack">
      <section class="panel">
        <div class="section-head">
          <span class="section-icon"><AppIcon name="type" /></span>
          <div>
            <h2>Text</h2>
            <p>Applies to every terminal you open. The terminal itself stays dark.</p>
          </div>
        </div>
        <div v-if="terminalError" class="banner error" role="alert">
          <AppIcon name="warning" /><span>{{ terminalError }}</span>
        </div>
        <div class="settings-rows">
          <div class="settings-row">
            <div class="settings-row-text">
              <strong>Font size</strong>
              <span>Between 10 and 24 pixels.</span>
            </div>
            <div class="stepper" role="group" aria-label="Font size">
              <button
                type="button"
                class="icon-button small"
                aria-label="Smaller text"
                :disabled="terminal.fontSize <= 10 || props.preferencesBusy"
                @click="patchTerminal({ fontSize: terminal.fontSize - 1 })"
              >
                <AppIcon name="minus" />
              </button>
              <span class="stepper-value mono">{{ terminal.fontSize }} px</span>
              <button
                type="button"
                class="icon-button small"
                aria-label="Larger text"
                :disabled="terminal.fontSize >= 24 || props.preferencesBusy"
                @click="patchTerminal({ fontSize: terminal.fontSize + 1 })"
              >
                <AppIcon name="plus" />
              </button>
            </div>
          </div>
          <pre class="terminal-preview" :data-size="terminal.fontSize" aria-hidden="true">
$ atum-remote status
device   VULCAN        online
session  terminal      <span class="terminal-preview-ok">ready</span></pre>
        </div>
      </section>
      <section class="panel">
        <div class="section-head">
          <span class="section-icon"><AppIcon name="keyboard" /></span>
          <div>
            <h2>Key bar</h2>
            <p>
              Esc, Tab, Ctrl combinations and arrows above the keyboard. Automatic shows it on touch
              screens.
            </p>
          </div>
        </div>
        <fieldset class="segmented" :disabled="props.preferencesBusy">
          <legend class="sr-only">Key bar</legend>
          <label v-for="option in keyBarOptions" :key="option.value">
            <input v-model="keyBarChoice" type="radio" name="key-bar" :value="option.value" />
            {{ option.label }}
          </label>
        </fieldset>
      </section>
    </div>

    <!-- Connections (installed app) -->
    <div v-else-if="active === 'connections'" class="page-stack">
      <section class="panel">
        <div class="section-head">
          <span class="section-icon"><AppIcon name="key" /></span>
          <div>
            <h2>Connections on this computer</h2>
            <p>
              Saved devices use your existing SSH configuration, including its relay, key and pinned
              host identity. Each connection opens in its own window; close the window to end it.
            </p>
          </div>
        </div>
        <div class="callout">
          <AppIcon name="info" />
          <div>
            <strong>Owner access client required</strong>
            <p>
              Install the matching atum-remote-access executable beside the app executable, then
              save the SSH alias you already use to reach each device.
            </p>
          </div>
        </div>
      </section>
    </div>

    <!-- Updates (installed app) -->
    <div v-else-if="active === 'updates'" class="page-stack">
      <NativeUpdatesPanel :updater="props.adapter.updater" />
    </div>

    <!-- About -->
    <div v-else class="page-stack">
      <section class="panel about-panel">
        <div class="section-head">
          <span class="section-icon"><BrandMark /></span>
          <div>
            <h2>About ATUM Remote</h2>
            <p>
              A private remote access tool for your own devices and the people you invite. Made by
              ATUM.
            </p>
          </div>
        </div>
        <dl class="facts">
          <div>
            <dt>This client</dt>
            <dd>{{ web ? 'Website' : 'Installed app' }}</dd>
          </div>
          <div v-if="!web && nativeUpdates.state.result">
            <dt>Version</dt>
            <dd class="mono">{{ nativeUpdates.state.result.currentVersion }}</dd>
          </div>
          <div>
            <dt>Appearance</dt>
            <dd>Use the theme switch at the bottom of the sidebar</dd>
          </div>
        </dl>
        <p v-if="web" class="about-link">
          <a href="https://atum.tech" rel="noreferrer">atum.tech<AppIcon name="external" /></a>
        </p>
        <p v-else class="about-link mono">atum.tech</p>
      </section>
    </div>
  </div>
</template>
